import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative block w-full overflow-hidden rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="relative h-[320px] md:h-[420px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 黑料大全`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
                {article.categoryName}
              </span>
              {article.isHot && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
                  热门
                </span>
              )}
            </div>
            <h2 className="text-xl md:text-3xl font-bold text-white mb-2 line-clamp-2 font-heading group-hover:text-primary-200 transition-colors">
              {article.title}
            </h2>
            <p className="text-sm md:text-base text-white/70 line-clamp-2 mb-3">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-white/50 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.viewCount >= 10000
                  ? `${(article.viewCount / 10000).toFixed(1)}万`
                  : article.viewCount}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {article.commentCount >= 10000
                  ? `${(article.commentCount / 10000).toFixed(1)}万`
                  : article.commentCount}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-200/70 transition-colors"
      >
        <div className="w-24 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 黑料大全`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500 mt-1">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount >= 10000
                ? `${(article.viewCount / 10000).toFixed(1)}万`
                : article.viewCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  // default
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block w-full rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={`${article.title} - 黑料大全`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
            {article.categoryName}
          </span>
          {article.isHot && (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
              热门
            </span>
          )}
          {article.isNew && (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-600 text-background-50 whitespace-nowrap">
              新
            </span>
          )}
        </div>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="text-base md:text-lg font-bold text-foreground-900 line-clamp-2 mb-2 font-heading group-hover:text-primary-600 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount >= 10000
                ? `${(article.viewCount / 10000).toFixed(1)}万`
                : article.viewCount}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i>
            {article.commentCount >= 10000
              ? `${(article.commentCount / 10000).toFixed(1)}万`
              : article.commentCount}
          </span>
        </div>
      </div>
    </Link>
  );
}